import type { LoyaltyCatalogRuleRuntime, LoyaltyRuntimeItem } from "../gateway/local-projection-client";
import type { LocalAppliedPromotion, LocalCartDraft, LocalCartLine, LocalSaleQuote } from "../local-store/cart-state";

export type LocalLoyaltyEarnPreview = {
  ready: boolean;
  state: "PREVIEW_EARNABLE" | "PREVIEW_NOT_EARNABLE" | "PREVIEW_NEEDS_MORE_CONTEXT" | "PREVIEW_BLOCKED";
  reasonCode: string;
  previewTotalPoints: number;
  policyVersionId: string;
  policyVersionNo: number;
  resolvedScopeType: string;
  resolvedBranchId: string | null;
  amountBasis: string;
};

type EligibleLine = {
  line: LocalCartLine;
  rule: LoyaltyCatalogRuleRuntime | null;
};

function finite(value: unknown) {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : 0;
}

function ratioPoints(basis: number, source: number, points: number, rounding: string) {
  if (!(source > 0) || !(points > 0) || !(basis >= 0)) return 0;
  const raw = basis / source;
  const units = rounding === "CEIL" ? Math.ceil(raw) : rounding === "ROUND_HALF_UP" ? Math.round(raw) : Math.floor(raw);
  return Math.max(0, units) * points;
}

function moneyRound(value: number, scale: number) {
  const factor = 10 ** scale;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

function catalogMultiplier(rule: LoyaltyCatalogRuleRuntime | null) {
  if (!rule) return 1;
  if (rule.effect === "NO_EARN") return 0;
  if (rule.effect === "EARN_MULTIPLIER") return Math.max(0, Math.min(1, finite(rule.multiplier ?? 1)));
  return 1;
}

function promotionMultiplier(promotion: LocalAppliedPromotion) {
  const treatment = String(promotion.loyaltyEarnTreatment ?? "FULL_POINTS").toUpperCase();
  if (treatment === "NO_POINTS") return 0;
  if (treatment === "HALF_POINTS") return 0.5;
  return 1;
}

function cartThreshold(condition: unknown): number | null {
  if (!condition || typeof condition !== "object") return null;
  const node = condition as { kind?: unknown; code?: unknown; operator?: unknown; params?: Record<string, unknown>; children?: unknown[] };
  const kind = String(node.kind ?? "").toUpperCase();
  const code = String(node.code ?? "").toUpperCase();
  if (kind === "PREDICATE" && code === "CART_SUBTOTAL_GTE") {
    const amount = finite(node.params?.amount);
    return amount > 0 ? amount : null;
  }
  if (kind !== "GROUP" || String(node.operator ?? "").toUpperCase() !== "AND" || !Array.isArray(node.children)) return null;
  const thresholds = node.children.map(cartThreshold).filter((value): value is number => value != null);
  return thresholds.length === 1 ? thresholds[0] : null;
}

function promotionQualifyingPaidAmount(promotion: LocalAppliedPromotion, targetPostAmount: number) {
  const multiplier = promotionMultiplier(promotion);
  if (multiplier >= 1) return 0;

  const benefit = promotion.benefitDocument?.[0];
  if (!benefit) return null;
  const code = String(benefit.code ?? "").toUpperCase();
  const params = benefit.params ?? {};
  const discount = Math.max(0, finite(promotion.appliedDiscountAmount));

  if (
    cartThreshold(promotion.conditionDocument) != null
    && code === "FIXED_AMOUNT_OFF"
    && String(params.target ?? "").toUpperCase() === "ORDER"
  ) {
    return Math.max((cartThreshold(promotion.conditionDocument) ?? 0) - discount, 0);
  }

  if (code === "BUNDLE_PRICE") {
    const requiredQty = finite(params.required_qty);
    if (requiredQty !== 1) return null;
    return Math.max(targetPostAmount, 0);
  }

  let percent = 0;
  if (code === "BUY_X_GET_Y") percent = finite(params.reward_percent);
  else if (code === "PERCENT_OFF" || code === "CHEAPEST_ITEM_PERCENT_OFF") percent = finite(params.percent);
  else if (code === "CHEAPEST_ITEM_FREE") percent = 100;

  if (percent > 0 && percent <= 100) {
    return percent === 100 ? 0 : Math.max(discount * (100 - percent) / percent, 0);
  }

  if (code === "FIXED_AMOUNT_OFF" || code === "FIXED_AMOUNT_OFF_PER_UNIT") {
    return Math.max(targetPostAmount, 0);
  }

  return null;
}

function ruleForLine(runtime: LoyaltyRuntimeItem, line: LocalCartLine) {
  const rules = runtime.policy.catalog_rules ?? [];
  const matches = rules.filter((rule) => {
    if (rule.reference_type === "SELLABLE_VARIANT") return Boolean(line.menuVariantId) && rule.reference_id === line.menuVariantId;
    if (rule.reference_type === "MENU_ITEM") return rule.reference_id === line.menuItemId;
    return Boolean(line.menuCategoryId) && rule.reference_id === line.menuCategoryId;
  });
  matches.sort((a, b) => {
    const rank = (type: LoyaltyCatalogRuleRuntime["reference_type"]) => type === "SELLABLE_VARIANT" ? 1 : type === "MENU_ITEM" ? 2 : 3;
    return rank(a.reference_type) - rank(b.reference_type);
  });
  return matches[0] ?? null;
}

function blocked(runtime: LoyaltyRuntimeItem, reasonCode: string): LocalLoyaltyEarnPreview {
  return {
    ready: false,
    state: "PREVIEW_NEEDS_MORE_CONTEXT",
    reasonCode,
    previewTotalPoints: 0,
    policyVersionId: runtime.policy_version_id,
    policyVersionNo: runtime.policy_version_no,
    resolvedScopeType: runtime.resolved_scope_type,
    resolvedBranchId: runtime.resolved_branch_id,
    amountBasis: String(runtime.policy.eligibility?.amount_basis ?? "BEFORE_DISCOUNT").toUpperCase(),
  };
}

export function resolveLocalLoyaltyEarnPreview(input: {
  cart: LocalCartDraft;
  quote: LocalSaleQuote;
  runtime: LoyaltyRuntimeItem;
  finalPayableTotal: number;
}): LocalLoyaltyEarnPreview {
  const { cart, quote, runtime } = input;
  const policy = runtime.policy;
  const formula = policy.formula;
  const formulaType = String(formula.type ?? "").toUpperCase();
  const sourceValue = finite(formula.source_value);
  const pointsValue = finite(formula.points_value);
  const rounding = String(formula.rounding ?? "FLOOR").toUpperCase();
  const amountBasis = String(policy.eligibility?.amount_basis ?? "BEFORE_DISCOUNT").toUpperCase();
  const minimumUnitAmount = policy.eligibility?.minimum_unit_amount == null ? null : finite(policy.eligibility.minimum_unit_amount);
  const minimumOrderAmount = policy.eligibility?.minimum_order_amount == null ? null : finite(policy.eligibility.minimum_order_amount);
  const quantityPromotionTreatment = String(policy.eligibility?.quantity_promotion_treatment ?? "INCLUDE_PROMOTION_AFFECTED_UNITS").toUpperCase();
  const promotionEarnMode = String(policy.eligibility?.promotion_earn_mode ?? "USE_PROMOTION_TREATMENT").toUpperCase();
  const fractionalPointMode = String(policy.fractional_point_mode ?? "ROUND_POLICY").toUpperCase();
  const currencyScale = cart.lines[0]?.currencyScale ?? 2;

  const base = {
    policyVersionId: runtime.policy_version_id,
    policyVersionNo: runtime.policy_version_no,
    resolvedScopeType: runtime.resolved_scope_type,
    resolvedBranchId: runtime.resolved_branch_id,
    amountBasis,
  };

  if (!cart.selectedMember) {
    return { ready: true, state: "PREVIEW_NOT_EARNABLE", reasonCode: "NO_ACTIVE_MEMBER_CONTEXT", previewTotalPoints: 0, ...base };
  }
  if (runtime.program_status !== "ACTIVE" || runtime.local_evaluation_mode !== "PUBLISHED_POLICY_PREVIEW") {
    return { ready: false, state: "PREVIEW_BLOCKED", reasonCode: "LOYALTY_POLICY_NOT_READY", previewTotalPoints: 0, ...base };
  }
  if (!cart.lines.length || !(sourceValue > 0) || !(pointsValue > 0)) return blocked(runtime, "LOYALTY_POLICY_NOT_READY");
  if (!["ELIGIBLE_QUANTITY_RATIO", "CATALOG_WEIGHTED", "COMMERCE_AMOUNT_RATIO", "ORDER_COUNT_RATIO"].includes(formulaType)) {
    return blocked(runtime, "LOYALTY_FORMULA_LOCAL_RUNTIME_NOT_SUPPORTED");
  }
  if (amountBasis === "FINAL_PAYABLE" && minimumUnitAmount != null) return blocked(runtime, "LOYALTY_FINAL_PAYABLE_MINIMUM_UNIT_NOT_SUPPORTED");

  const orderBasis = amountBasis === "FINAL_PAYABLE"
    ? input.finalPayableTotal
    : amountBasis === "AFTER_DISCOUNT"
      ? quote.postPromotionTotal
      : quote.prePromotionTotal;
  if (minimumOrderAmount != null && orderBasis < minimumOrderAmount) {
    return { ready: true, state: "PREVIEW_NOT_EARNABLE", reasonCode: "BELOW_MINIMUM_ORDER_AMOUNT", previewTotalPoints: 0, ...base };
  }

  const eligible: EligibleLine[] = [];
  for (const line of cart.lines) {
    const rule = ruleForLine(runtime, line);
    if (rule?.effect === "NO_EARN") continue;
    eligible.push({ line, rule });
  }
  if (!eligible.length) {
    return { ready: true, state: "PREVIEW_NOT_EARNABLE", reasonCode: "NO_ELIGIBLE_AMOUNT", previewTotalPoints: 0, ...base };
  }

  if (promotionEarnMode === "NORMAL_PRICE_ONLY") {
    if (formulaType !== "COMMERCE_AMOUNT_RATIO") {
      return blocked(runtime, "NORMAL_PRICE_ONLY_REQUIRES_COMMERCE_AMOUNT_RATIO");
    }

    const applied = quote.appliedPromotions;
    if (applied.some((promotion) => !promotion.applicationCoverage?.ready)) {
      return blocked(runtime, "PROMOTION_APPLICATION_COVERAGE_NOT_READY");
    }

    const orderCovered = applied.some((promotion) => promotion.applicationCoverage?.scope === "ORDER");
    const coveredUnitsByLine = new Map<string, Set<number>>();
    if (!orderCovered) {
      for (const promotion of applied) {
        const coverage = promotion.applicationCoverage;
        if (!coverage || coverage.scope !== "UNIT") continue;
        for (const covered of coverage.coveredUnits) {
          const set = coveredUnitsByLine.get(covered.lineId) ?? new Set<number>();
          for (const unitNo of covered.unitNos) {
            if (!Number.isInteger(unitNo) || unitNo < 1) {
              return blocked(runtime, "PROMOTION_APPLICATION_COVERAGE_INVALID");
            }
            set.add(unitNo);
          }
          coveredUnitsByLine.set(covered.lineId, set);
        }
      }
    }

    let weightedBasis = 0;
    for (const { line, rule } of eligible) {
      const multiplier = catalogMultiplier(rule);
      if (multiplier <= 0) continue;

      const covered = orderCovered ? line.quantity : (coveredUnitsByLine.get(line.lineId)?.size ?? 0);
      if (!orderCovered && covered > 0 && (!Number.isInteger(line.quantity) || covered > line.quantity)) {
        return blocked(runtime, "PROMOTION_APPLICATION_UNIT_CONTEXT_REQUIRED");
      }

      const normalQuantity = Math.max(line.quantity - covered, 0);
      if (normalQuantity <= 0) continue;
      if (minimumUnitAmount != null && line.unitPrice < minimumUnitAmount) continue;
      weightedBasis += line.unitPrice * normalQuantity * multiplier;
    }

    if (amountBasis === "FINAL_PAYABLE") {
      weightedBasis = quote.postPromotionTotal > 0
        ? weightedBasis * input.finalPayableTotal / quote.postPromotionTotal
        : 0;
    }

    const total = fractionalPointMode === "PRESERVE_DECIMAL"
      ? moneyRound(Math.max(weightedBasis, 0) / sourceValue * pointsValue, 6)
      : ratioPoints(weightedBasis, sourceValue, pointsValue, rounding);

    return {
      ready: true,
      state: total > 0 ? "PREVIEW_EARNABLE" : "PREVIEW_NOT_EARNABLE",
      reasonCode: total > 0 ? "DEFAULT_FORMULA_APPLIED" : "FORMULA_NOT_SATISFIED",
      previewTotalPoints: total,
      ...base,
    };
  }

  const hasPromotionDiscount = quote.promotionDiscountTotal > 0.000001;

  if (minimumUnitAmount != null && amountBasis !== "BEFORE_DISCOUNT" && hasPromotionDiscount) {
    // Local quote currently keeps authoritative order totals but not every server-only
    // promotion allocation. Do not invent a per-line average for eligibility.
    return blocked(runtime, "LOYALTY_LINE_AFTER_DISCOUNT_CONTEXT_REQUIRED");
  }

  if (["ELIGIBLE_QUANTITY_RATIO", "CATALOG_WEIGHTED"].includes(formulaType)) {
    if (quantityPromotionTreatment === "EXCLUDE_PROMOTION_AFFECTED_UNITS" && hasPromotionDiscount) {
      return blocked(runtime, "PROMOTION_UNIT_ALLOCATION_REQUIRED");
    }
    let total = 0;
    for (const { line, rule } of eligible) {
      const unitBasis = line.unitPrice;
      if (minimumUnitAmount != null && unitBasis < minimumUnitAmount) continue;
      if (rule?.effect === "POINT_OVERRIDE") {
        total += ratioPoints(
          line.quantity,
          finite(rule.formula?.source_value ?? 1),
          finite(rule.formula?.points_value ?? 0),
          String(rule.formula?.rounding ?? "FLOOR").toUpperCase(),
        );
      } else {
        total += ratioPoints(line.quantity, sourceValue, pointsValue, rounding);
      }
    }
    return {
      ready: true,
      state: total > 0 ? "PREVIEW_EARNABLE" : "PREVIEW_NOT_EARNABLE",
      reasonCode: total > 0 ? "DEFAULT_FORMULA_APPLIED" : "FORMULA_NOT_SATISFIED",
      previewTotalPoints: total,
      ...base,
    };
  }

  if (formulaType === "ORDER_COUNT_RATIO") {
    if (sourceValue > 1) return blocked(runtime, "FORMULA_REQUIRES_LOYALTY_ACCOUNT_PROGRESS");
    const total = ratioPoints(1, sourceValue, pointsValue, rounding);
    return {
      ready: true,
      state: total > 0 ? "PREVIEW_EARNABLE" : "PREVIEW_NOT_EARNABLE",
      reasonCode: total > 0 ? "DEFAULT_FORMULA_APPLIED" : "FORMULA_NOT_SATISFIED",
      previewTotalPoints: total,
      ...base,
    };
  }

  const linePostAmounts = new Map(
    cart.lines.map((line) => {
      const allocatedDiscount = quote.appliedPromotions.reduce(
        (sum, promotion) => sum + (promotion.lineAllocations ?? [])
          .filter((allocation) => allocation.lineId === line.lineId)
          .reduce((lineSum, allocation) => lineSum + finite(allocation.amount), 0),
        0,
      );
      return [
        line.lineId,
        moneyRound(Math.max(line.unitPrice * line.quantity - allocatedDiscount, 0), currencyScale),
      ] as const;
    }),
  );

  const reducedPromotions = quote.appliedPromotions
    .map((promotion) => ({ promotion, multiplier: promotionMultiplier(promotion) }))
    .filter((item) => item.multiplier < 1);

  if (amountBasis === "BEFORE_DISCOUNT" && reducedPromotions.length > 0) {
    return blocked(runtime, "LOYALTY_PROMOTION_EARN_RATE_REQUIRES_POST_DISCOUNT_BASIS");
  }

  const portionsByLine = new Map<string, Array<{ multiplier: number; amount: number; promotionVersionId: string }>>();
  for (const item of reducedPromotions) {
    const targetIds = [...new Set(
      (item.promotion.lineAllocations ?? [])
        .filter((allocation) => finite(allocation.amount) > 0)
        .map((allocation) => allocation.lineId),
    )].sort();
    const targetPost = targetIds.reduce((sum, lineId) => sum + (linePostAmounts.get(lineId) ?? 0), 0);
    const qualifying = promotionQualifyingPaidAmount(item.promotion, targetPost);
    if (qualifying == null) return blocked(runtime, "PROMOTION_LOYALTY_EARN_FACT_NOT_READY");
    if (qualifying <= 0) continue;
    if (targetPost <= 0) return blocked(runtime, "PROMOTION_LOYALTY_EARN_FACT_NOT_READY");

    for (const lineId of targetIds) {
      const linePost = linePostAmounts.get(lineId) ?? 0;
      const amount = qualifying * linePost / targetPost;
      const list = portionsByLine.get(lineId) ?? [];
      list.push({
        multiplier: item.multiplier,
        amount,
        promotionVersionId: item.promotion.promotionVersionId,
      });
      portionsByLine.set(lineId, list);
    }
  }

  let weightedBasis = 0;
  for (const { line, rule } of eligible) {
    const multiplier = catalogMultiplier(rule);
    const lineBasis = amountBasis === "BEFORE_DISCOUNT"
      ? line.unitPrice * line.quantity
      : (linePostAmounts.get(line.lineId) ?? 0);
    let remaining = Math.max(lineBasis, 0);
    let weightedLine = 0;
    const portions = [...(portionsByLine.get(line.lineId) ?? [])]
      .sort((a, b) => (a.multiplier - b.multiplier) || a.promotionVersionId.localeCompare(b.promotionVersionId));

    for (const portion of portions) {
      if (remaining <= 0) break;
      const amount = Math.min(Math.max(portion.amount, 0), remaining);
      weightedLine += amount * Math.min(multiplier, portion.multiplier);
      remaining -= amount;
    }
    weightedLine += remaining * multiplier;
    weightedBasis += weightedLine;
  }

  if (amountBasis === "FINAL_PAYABLE") {
    weightedBasis = quote.postPromotionTotal > 0
      ? weightedBasis * input.finalPayableTotal / quote.postPromotionTotal
      : 0;
  }

  const total = fractionalPointMode === "PRESERVE_DECIMAL"
    ? moneyRound(Math.max(weightedBasis, 0) / sourceValue * pointsValue, 6)
    : ratioPoints(weightedBasis, sourceValue, pointsValue, rounding);

  return {
    ready: true,
    state: total > 0 ? "PREVIEW_EARNABLE" : "PREVIEW_NOT_EARNABLE",
    reasonCode: total > 0 ? "DEFAULT_FORMULA_APPLIED" : "FORMULA_NOT_SATISFIED",
    previewTotalPoints: total,
    ...base,
  };
}
